// frontend/src/views/Login.vue
<template>
    <v-container>
      <v-row justify="center">
        <v-col cols="12" sm="8" md="4">
          <v-card>
            <v-card-title class="text-h5 text-center pa-4">Giriş Yap</v-card-title>
            <v-card-text>
               <!-- Generic Error Alert (Uses global this.error) -->
               <v-alert v-if="error && !showResendVerification" type="error" dense dismissible class="mb-4">
                {{ error }}
              </v-alert>
               <!-- Registration Success Alert -->
               <v-alert v-if="registeredMessage" type="success" dense class="mb-4">
                 Kayıt başarılı! Lütfen giriş yapın.
               </v-alert>
               <!-- Verification Needed Alert (Uses global this.error) -->
               <v-alert v-if="showResendVerification" type="warning" dense class="mb-4">
                 {{ error }} <!-- Display the specific error message from Vuex state -->
                 <div class="mt-2">
                   <v-btn small text color="primary" @click="resendVerificationEmail" :loading="resending" :disabled="resending">
                     Doğrulama E-postasını Yeniden Gönder
                   </v-btn>
                 </div>
               </v-alert>
               <!-- Resend Success/Error Message (Uses local resendMessage) -->
               <v-alert v-if="resendMessage" :type="resendError ? 'error' : 'info'" dense class="mb-4">
                   {{ resendMessage }}
               </v-alert>

              <v-form @submit.prevent="handleLogin" ref="loginForm">
                <v-text-field
                  v-model="emailOrUsername"
                  label="E-posta veya Kullanıcı Adı"
                  required
                  :rules="[rules.required]"
                  prepend-icon="mdi-account"
                  @input="clearVerificationMessages"
                ></v-text-field>
                <v-text-field
                  v-model="password"
                  label="Şifre"
                  type="password"
                  required
                  :rules="[rules.required]"
                  prepend-icon="mdi-lock"
                  @input="clearVerificationMessages"
                ></v-text-field>
                <v-btn
                  type="submit"
                  color="primary"
                  block
                  large
                  class="mt-4"
                  :loading="isLoading"
                  :disabled="isLoading"
                >
                  Giriş Yap
                </v-btn>
              </v-form>

               <v-divider class="my-4"></v-divider>

               <v-btn
                 color="red"
                 dark
                 block
                 large
                 :href="googleLoginUrl"
                 class="mt-2"
               >
                 <v-icon left>mdi-google</v-icon>
                 Google ile Giriş Yap
               </v-btn>

            </v-card-text>
             <v-card-actions class="justify-space-between pb-4 px-4">
                  <!-- Forgot Password Link -->
                  <v-btn text small to="/forgot-password" color="primary">Şifremi Unuttum?</v-btn>
                  <!-- Register Link -->
                  <span>Hesabınız yok mu? <v-btn small text color="primary" to="/register" class="pl-1">Kayıt Olun</v-btn></span>
              </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </template>

  <script>
  import { mapActions, mapGetters, mapMutations } from 'vuex';
  import authService from '@/services/authService'; // Import authService

  export default {
    name: 'LoginView',
    data() {
      return {
        emailOrUsername: '',
        password: '',
        rules: {
          required: value => !!value || 'Bu alan zorunludur.',
        },
        registeredMessage: false,
        // Local state for verification flow
        showResendVerification: false,
        resending: false,
        resendMessage: null,
        resendError: false,
      };
    },
    computed: {
      // Use global loading/error for the main login action
      ...mapGetters(['isLoading', 'error']),
       googleLoginUrl() {
          const backendBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5001/api';
          return `${backendBaseUrl}/auth/google`;
      }
    },
     mounted() {
      if (this.$route.query.registered === 'true') {
        this.registeredMessage = true;
      }
      this.CLEAR_ERROR(); // Use mutation
      this.clearVerificationMessages(); // Clear local messages too
    },
    methods: {
      ...mapActions('auth', ['login']),
      ...mapMutations(['CLEAR_ERROR', 'SET_ERROR']), // Map mutations

      clearVerificationMessages() {
          this.showResendVerification = false;
          this.resendMessage = null;
          this.resendError = false;
          // Clear global error only if it's the verification error
          if (this.error && this.error.includes('doğrulanmadı')) {
              this.CLEAR_ERROR();
          }
      },

      async handleLogin() {
         this.CLEAR_ERROR();
         this.clearVerificationMessages();

        const { valid } = await this.$refs.loginForm.validate();
        if (valid) {
           try {
             // Action handles success redirect
             await this.login({
               emailOrUsername: this.emailOrUsername,
               password: this.password,
             });
           } catch (err) {
             // Catch error re-thrown by the action
             if (err?.response?.data?.code === 'ACCOUNT_NOT_VERIFIED') {
                 this.showResendVerification = true;
                 // Error message is already set in Vuex store by the action
             } else {
                 // Other errors are also set in Vuex store by the action
                 console.error("Login component caught error:", err);
             }
           }
        }
      },

      async resendVerificationEmail() {
        // ... (validation for emailOrUsername remains the same) ...
        if (!this.emailOrUsername) {
            this.resendMessage = 'Lütfen e-posta adresinizi veya kullanıcı adınızı girin.';
            this.resendError = true;
            return;
        }
        this.resending = true;
        this.resendMessage = null;
        this.resendError = false;
        this.CLEAR_ERROR();

        try {
            let emailToSend = this.emailOrUsername;
            if (!emailToSend.includes('@')) {
                this.resendMessage = 'Doğrulama e-postası göndermek için lütfen e-posta adresinizi girin.';
                this.resendError = true;
                this.resending = false;
                return;
            }

            // --- Use authService ---
            const response = await authService.resendVerification({ email: emailToSend });
            // --- End Use authService ---

            this.resendMessage = response.data.message;
            this.resendError = false;
            this.showResendVerification = false;

        } catch (err) {
             this.resendError = true;
             if (err.response && err.response.data) {
                this.resendMessage = err.response.data.message || 'E-posta gönderilirken bir hata oluştu.';
             } else {
                this.resendMessage = 'E-posta gönderilirken bir ağ hatası veya bilinmeyen bir hata oluştu.';
             }
             console.error('Resend verification error:', err);
        } finally {
            this.resending = false;
        }
    }
    },
  };
  </script>
